import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    FaArchive, FaDownload, FaEdit, FaFilePdf, FaFolderOpen, FaPlus,
    FaRegStar, FaSearch, FaStar, FaTimes, FaEyeSlash, FaSlidersH,
} from "react-icons/fa";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useScrollLock } from "../../../../../_CORE/APP/Templates/_Hooks/useScrollLock";
import { REACT_HOT_TOAST_ERROR } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { downloadFilesAsZip } from "../../../../_UI/FileBatchDownload";
import { onMatchesSearch } from "../../../../_UI/SearchFilter";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { LIBRARY_CONSTANTS } from "../_MODULES/_API";

const endpointFor = (id, download = false) => {
    const base = API_CORE.ENDPOINTS[LIBRARY_CONSTANTS.PDF_FILE].replace(":id", String(id));
    return download ? `${base}?download=1` : base;
};

const filenameFor = (pdf) => pdf?.originalName || `${pdf?.title || "documento"}.pdf`;

const formatSize = (bytes) => {
    const value = Number(bytes || 0);
    if (value < 1024) return `${value} B`;
    if (value < 1048576) return `${Math.round(value / 1024)} KB`;
    return `${(value / 1048576).toFixed(1)} MB`;
};

const loadLibraryPdfBlob = async (pdf, download = true) => {
    if (!pdf?.id) throw new Error("PDF sin identificador");
    const blob = await API_CORE.SERVICES.GET_RESOURCE(endpointFor(pdf.id, download), { responseType: "blob" });
    if (!(blob instanceof Blob)) throw new Error(`No se pudo obtener ${filenameFor(pdf)}`);
    return blob;
};

export const downloadLibraryFolder = async (group, documents = []) => downloadFilesAsZip({
    files: documents,
    loadBlob: (pdf) => loadLibraryPdfBlob(pdf, true),
    fileName: `${group?.name || "biblioteca"}-PDFs.zip`,
    successMessage: `Carpeta descargada: ${documents.length} PDF${documents.length === 1 ? "" : "s"}`,
    errorMessage: "No se pudo descargar la carpeta completa.",
});

export const downloadLibraryPdf = async (pdf) => {
    if (!pdf?.id) return false;
    let blob;
    try {
        blob = await loadLibraryPdfBlob(pdf, true);
    } catch {
        REACT_HOT_TOAST_ERROR("No se pudo descargar el PDF");
        return false;
    }
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = filenameFor(pdf);
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
};

export const LibraryPdfReaderModal = ({
    group,
    documents = [],
    document: initialPdf = null,
    isOpen,
    onClose,
    onEdit,
    onOpened,
    onUpload,
    onToggleFavorite,
    onArchive,
    isLoading = false,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedId, setSelectedId] = useState("");
    const [search, setSearch] = useState("");
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", author: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const advancedCount = [
        advanced.favorite !== "ALL", String(advanced.author || "").trim(), advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact,
    ].filter(Boolean).length;
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [downloadingAll, setDownloadingAll] = useState(false);

    const pdfs = useMemo(() => Array.isArray(documents) ? documents : [], [documents]);
    const visiblePdfs = useMemo(() => pdfs.filter((pdf) => {
        if (!onMatchesSearch(search, [pdf.title, pdf.author, pdf.description, pdf.originalName])) return false;
        if (advanced.favorite !== "ALL" && (!!pdf.isFavorite !== (advanced.favorite === "YES"))) return false;
        if (String(advanced.author || "").trim() && !onMatchesSearch(advanced.author, [pdf.author])) return false;
        const dateValue = advanced.dateField === "CREATED" ? pdf.createdAt : pdf.updatedAt;
        return onDateMatches(dateValue, advanced.dateRange, advanced.dateExact);
    }), [pdfs, search, advanced]);
    const selectedPdf = useMemo(
        () => visiblePdfs.find((pdf) => String(pdf.id) === String(selectedId)) || null,
        [visiblePdfs, selectedId],
    );

    useEffect(() => {
        if (!isOpen) return;
        setSearch("");
        setAdvanced({ favorite: "ALL", author: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
        setAdvancedOpen(searchParams.get("fileAdvanced") === "1");
        // Prioridad a la URL para que F5 restaure exactamente el PDF seleccionado.
        // initialPdf sólo se usa cuando una acción explícita abrió el lector con un archivo.
        const routed = searchParams.get("file");
        setSelectedId(routed || (initialPdf?.id ? String(initialPdf.id) : ""));
    }, [isOpen, group?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOpen || isLoading) return;
        const routed = searchParams.get("file");
        if (!routed) return;
        if (pdfs.some((pdf) => String(pdf.id) === String(routed))) {
            setSelectedId(String(routed));
        }
        // No borres el id de la URL mientras hidratan los datos.
        // Si la API tarda un render, el registro sigue siendo recuperable al recargar.
    }, [isOpen, isLoading, searchParams, pdfs, setSearchParams]);

    const load = useCallback(async () => {
        setBlobUrl((current) => {
            if (current) window.URL.revokeObjectURL(current);
            return null;
        });
        setError("");
        if (!selectedPdf?.id || !isOpen) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const blob = await API_CORE.SERVICES.GET_RESOURCE(endpointFor(selectedPdf.id), { responseType: "blob" });
        if (!(blob instanceof Blob)) {
            setError("No se pudo abrir el PDF.");
            setLoading(false);
            return;
        }
        setBlobUrl(window.URL.createObjectURL(blob));
        setLoading(false);
        onOpened?.(selectedPdf);
    }, [selectedPdf?.id, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useScrollLock(isOpen);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                window.document.getElementById("library-reader-search")?.focus();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen]);

    useEffect(() => () => {
        if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    }, [blobUrl]);

    const onDownloadAll = async () => {
        if (!pdfs.length || downloadingAll) return;
        setDownloadingAll(true);
        try {
            await downloadLibraryFolder(group, pdfs);
        } finally {
            setDownloadingAll(false);
        }
    };

    const updateRouteState = (patch = {}) => {
        const next = new URLSearchParams(window.location.search);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") next.delete(key);
            else next.set(key, String(value));
        });
        setSearchParams(next, { replace: true });
    };

    const selectPdf = (id) => {
        setSelectedId(String(id));
        updateRouteState({ file: id, preview: null });
    };

    const hidePreview = () => {
        setSelectedId("");
        updateRouteState({ file: null, preview: null });
    };

    const toggleAdvanced = () => {
        const next = !advancedOpen;
        setAdvancedOpen(next);
        updateRouteState({ fileAdvanced: next ? "1" : null });
    };

    if (!isOpen || !group) return null;

    return (
        <div className="LStudio__library_reader_backdrop" role="presentation">
            <section className="LStudio__library_reader" role="dialog" aria-modal="true" aria-label={`Biblioteca · ${group.name}`}>
                <header className="LStudio__library_reader_header">
                    <div className="LStudio__library_reader_identity">
                        <span style={{ "--library-group-color": group.color || "#7b8498" }}><FaFolderOpen /></span>
                        <div>
                            <strong>{group.name}</strong>
                            <small>{selectedPdf ? `${selectedPdf.title} · ${filenameFor(selectedPdf)}` : `${pdfs.length} PDFs en esta carpeta`}</small>
                        </div>
                    </div>

                    <div className="LStudio__library_reader_actions">
                        {pdfs.length > 0 && (
                            <AppButton variant="outline" tone="neutral" icon={<FaDownload />}
                                onClick={onDownloadAll} isLoading={downloadingAll}>
                                Descargar carpeta
                            </AppButton>
                        )}
                        {selectedPdf && (
                            <>
                                <AppButton variant="outline" tone="neutral" icon={<FaDownload />} onClick={() => downloadLibraryPdf(selectedPdf)}>Descargar</AppButton>
                                <AppButton variant="outline" tone="neutral" icon={<FaEdit />} onClick={() => onEdit?.(selectedPdf)}>Editar</AppButton>
                                <CardActionMenu
                                    title="Opciones del PDF"
                                    ariaLabel="Opciones del PDF"
                                    items={[
                                        {
                                            key: "favorite",
                                            label: selectedPdf.isFavorite ? "Quitar de favoritos" : "Marcar como favorito",
                                            icon: selectedPdf.isFavorite ? <FaStar /> : <FaRegStar />,
                                            onClick: () => onToggleFavorite?.(selectedPdf),
                                        },
                                        { divider: true },
                                        { key: "archive", label: "Archivar PDF", icon: <FaArchive />, tone: "danger", onClick: () => onArchive?.(selectedPdf) },
                                    ]}
                                />
                            </>
                        )}
                        {selectedPdf && <AppButton variant="ghost" tone="neutral" icon={<FaEyeSlash />} onClick={hidePreview}>Ocultar</AppButton>}
                        <button type="button" className="LStudio__library_reader_close" onClick={onClose} aria-label="Cerrar lector"><FaTimes /></button>
                    </div>
                </header>

                <div className="LStudio__library_reader_workspace">
                    <aside className="LStudio__library_reader_sidebar">
                        <div className="LStudio__library_reader_folder_meta">
                            <span className="LStudio__library_reader_folder_icon" style={{ "--library-group-color": group.color || "#7b8498" }}><FaFolderOpen /></span>
                            <div><strong>{group.name}</strong><small>{pdfs.length} PDF{pdfs.length === 1 ? "" : "s"}</small></div>
                        </div>

                        <div className="LStudio__library_reader_filterbar">
                            <div className="LStudio__library_reader_search">
                                <FaSearch />
                                <input
                                    id="library-reader-search"
                                    type="search"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Buscar archivos... Ctrl+K"
                                />
                            </div>
                            <button type="button" className="LStudio__library_reader_filter_toggle" data-open={advancedOpen ? "true" : "false"}
                                onClick={toggleAdvanced} title="Filtros avanzados" aria-label="Filtros avanzados"><FaSlidersH /></button>
                        </div>

                        <AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} className="LStudio__library_reader_advanced"
                            onClear={() => setAdvanced({ favorite: "ALL", author: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null })}>
                            <FieldSelect id="library_reader_favorite" label="Favorito" value={advanced.favorite}
                                onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))} options={[
                                    { value: "ALL", label: "Todos" }, { value: "YES", label: "Favoritos" }, { value: "NO", label: "No favoritos" },
                                ]} />
                            <FieldInput id="library_reader_author" label="Autor" value={advanced.author}
                                onChange={(event) => setAdvanced((current) => ({ ...current, author: event.target.value }))} />
                            <FieldSelect id="library_reader_date" label="Fecha" value={advanced.dateField}
                                onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))} options={[
                                    { value: "UPDATED", label: "Actualización" }, { value: "CREATED", label: "Creación" },
                                ]} />
                            <FieldDate id="library_reader_from" label="Desde" value={advanced.dateRange?.[0] || null}
                                onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                            <FieldDate id="library_reader_to" label="Hasta" value={advanced.dateRange?.[1] || null}
                                onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                            <FieldDate id="library_reader_exact" label="Fecha exacta" value={advanced.dateExact}
                                onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                        </AdvancedFilterPanel>

                        <AppButton
                            size="sm"
                            variant="outline"
                            tone="neutral"
                            icon={<FaPlus />}
                            onClick={() => onUpload?.(group)}
                        >
                            Subir PDF a esta carpeta
                        </AppButton>

                        <div className="LStudio__library_reader_list" role="list">
                            {visiblePdfs.map((pdf) => (
                                <button
                                    key={pdf.id}
                                    type="button"
                                    className="LStudio__library_reader_item"
                                    data-active={String(pdf.id) === String(selectedId) ? "true" : undefined}
                                    onClick={() => selectPdf(pdf.id)}
                                >
                                    <span><FaFilePdf /></span>
                                    <div>
                                        <strong title={pdf.title}>{pdf.title}</strong>
                                        <small>{[pdf.author, formatSize(pdf.size)].filter(Boolean).join(" · ") || filenameFor(pdf)}</small>
                                    </div>
                                    {pdf.isFavorite && <FaStar className="LStudio__library_reader_item_favorite" aria-label="Favorito" />}
                                </button>
                            ))}

                            {pdfs.length > 0 && visiblePdfs.length === 0 && (
                                <div className="LStudio__library_reader_list_empty">
                                    <FaSearch />
                                    <strong>Sin resultados</strong>
                                    <span>Probá con otro nombre, autor o archivo.</span>
                                </div>
                            )}

                            {pdfs.length === 0 && (
                                <div className="LStudio__library_reader_list_empty">
                                    <FaFilePdf />
                                    <strong>Carpeta vacía</strong>
                                    <span>Subí el primer PDF para verlo acá.</span>
                                </div>
                            )}
                        </div>
                    </aside>

                    <main className="LStudio__library_reader_body">
                        {!selectedPdf && (
                            <div className="LStudio__library_reader_state">
                                <FaFilePdf />
                                <strong>No hay un PDF seleccionado</strong>
                                <span>Elegí un archivo de la lista de la izquierda o subí uno nuevo.</span>
                            </div>
                        )}
                        {selectedPdf && loading && (
                            <div className="LStudio__library_reader_state"><FaFilePdf /><strong>Cargando PDF...</strong><span>Preparando el documento para lectura.</span></div>
                        )}
                        {selectedPdf && !loading && error && (
                            <div className="LStudio__library_reader_state">
                                <FaFilePdf />
                                <strong>No se pudo mostrar el PDF</strong>
                                <span>{error}</span>
                                <AppButton icon={<FaDownload />} onClick={() => downloadLibraryPdf(selectedPdf)}>Descargar PDF</AppButton>
                            </div>
                        )}
                        {selectedPdf && !loading && !error && blobUrl && (
                            <iframe className="LStudio__library_reader_frame" src={blobUrl} title={selectedPdf.title} scrolling="auto" />
                        )}
                    </main>
                </div>
            </section>
        </div>
    );
};
